import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';
import { formatDate } from '@/utils/dateFormat';

export default function TechnicianDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [earnings, setEarnings] = useState({ total_earnings: 0, earnings_count: 0, earnings: [] });
  const [loading, setLoading] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [serviceData, setServiceData] = useState({
    service_mileage: '',
    oil_type_used: 'Synthetic Oil'
  });
  const [pickupOrders, setPickupOrders] = useState([]);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ratingData, setRatingData] = useState({
    product_quality: 5,
    service_quality: 5,
    timeliness: 5,
    comment: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, earningsRes, pickupsRes, pendingRatingsRes] = await Promise.all([
        axios.get(`${API}/technician/appointments`),
        axios.get(`${API}/technician/earnings`),
        axios.get(`${API}/technician/pickup-orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/technician/pending-ratings`).catch(() => ({ data: [] }))
      ]);
      setAppointments(appointmentsRes.data);
      setEarnings(earningsRes.data);
      setPickupOrders(pickupsRes.data);
      setPendingRatings(pendingRatingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/technician/appointments/${appointmentId}/status?status=${status}`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const verifyCashPayment = async (appointmentId) => {
    try {
      await axios.put(`${API}/technician/appointments/${appointmentId}/verify-cash-payment`, {
        payment_status: 'verified'
      });
      toast.success('Cash payment verified');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  const openCompleteDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setServiceData({
      service_mileage: '',
      oil_type_used: appointment.preferred_oil_type || 'Synthetic Oil'
    });
    setCompleteDialogOpen(true);
  };

  const completeService = async () => {
    if (!serviceData.service_mileage) {
      toast.error('Please enter the current mileage');
      return;
    }

    try {
      const response = await axios.put(
        `${API}/technician/appointments/${selectedAppointment.id}/complete-service?service_mileage=${serviceData.service_mileage}&oil_type_used=${encodeURIComponent(serviceData.oil_type_used)}`
      );
      toast.success(`Service completed! You earned ₦${response.data.technician_earning.toLocaleString()}`);
      setCompleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete service');
    }
  };

  const confirmPickup = async (orderId) => {
    try {
      await axios.put(`${API}/technician/orders/${orderId}/confirm-pickup`);
      toast.success('Pickup confirmed!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm pickup');
    }
  };

  const openRatingDialog = (order) => {
    setSelectedOrder(order);
    setRatingData({ product_quality: 5, service_quality: 5, timeliness: 5, comment: '' });
    setRatingDialogOpen(true);
  };

  const submitRating = async () => {
    if (!selectedOrder) return;
    try {
      await axios.post(`${API}/technician/merchant-rating`, {
        order_id: selectedOrder.id,
        ...ratingData
      });
      toast.success('Rating submitted successfully!');
      setRatingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const todayAppointments = appointments.filter(a => a.date === today);
  const upcomingAppointments = appointments.filter(a => a.date > today);
  const pendingCashPayments = appointments.filter(a => 
    a.payment_method === 'cash' && 
    a.payment_status === 'pending'
  );
  const readyForService = appointments.filter(a => 
    a.payment_status === 'verified' && 
    a.status !== 'completed'
  );
  const readyPickups = pickupOrders.filter(o => o.status === 'ready_for_pickup');

  return (
    <div className="min-h-screen bg-background" data-testid="technician-dashboard">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-secondary font-bold uppercase tracking-wide">Technician Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <Button
                variant="outline"
                onClick={() => navigate('/marketer')}
                data-testid="view-commissions-btn"
                className="font-bold uppercase text-sm border-2 border-green-600 text-green-600 hover:bg-green-50 hidden sm:flex"
              >
                My Commissions
              </Button>
              <span className="text-sm font-medium text-muted-foreground hidden md:inline">Hello, {user?.name}</span>
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="font-bold uppercase text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-secondary text-white p-6 rounded-sm shadow-hard mb-8">
          <h1 className="text-2xl font-heading font-bold mb-1">My Assignments This Week</h1>
          <p className="text-sm opacity-90">View and manage your scheduled service appointments</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-secondary mb-2">{todayAppointments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Today&apos;s Jobs</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-primary mb-2">{appointments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">This Week</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-green-600 mb-2">₦{earnings.total_earnings.toLocaleString()}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Earnings</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-orange-500 mb-2">{pendingCashPayments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Cash to Verify</div>
          </div>
        </div>

        {/* Cash Payments to Verify */}
        {pendingCashPayments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="text-orange-500">💵</span> Cash Payments to Verify
            </h2>
            <div className="space-y-4">
              {pendingCashPayments.map((appointment) => (
                <div key={appointment.id} className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="font-bold text-foreground">{appointment.service_type}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(appointment.date)} at {appointment.time}</p>
                      <p className="text-sm text-muted-foreground">{appointment.location}</p>
                    </div>
                    <Button
                      onClick={() => verifyCashPayment(appointment.id)}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                    >
                      💵 Verify Cash Received
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ready for Service */}
        {readyForService.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="text-green-600">✅</span> Ready for Service (Payment Verified)
            </h2>
            <div className="space-y-4">
              {readyForService.map((appointment) => (
                <div key={appointment.id} className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="font-bold text-foreground">{appointment.service_type}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(appointment.date)} at {appointment.time}</p>
                      <p className="text-sm text-muted-foreground">{appointment.location}</p>
                      {appointment.preferred_oil_type && (
                        <p className="text-sm text-green-700 mt-1">Oil: {appointment.preferred_oil_type}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => openCompleteDialog(appointment)}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold"
                    >
                      ✓ Complete Service
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Merchant Pickup Orders */}
        {readyPickups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="text-purple-600">📦</span> Merchant Pickups Ready
            </h2>
            <div className="space-y-4">
              {readyPickups.map((order) => (
                <div key={order.id} className="bg-purple-50 border-2 border-purple-200 p-4 rounded-sm" data-testid="pickup-order-card">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="font-bold text-foreground">{order.merchant_name}</p>
                      <p className="text-sm text-muted-foreground">{order.merchant_address}</p>
                      <p className="text-sm text-muted-foreground">Phone: {order.merchant_phone}</p>
                      <div className="mt-2">
                        {order.items?.map((item, i) => (
                          <span key={i} className="text-sm mr-2">{item.quantity}x {item.brand} ({item.type})</span>
                        ))}
                      </div>
                      {order.total_amount && (
                        <p className="text-sm font-bold text-purple-700 mt-1">Value: ₦{order.total_amount.toLocaleString()}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => confirmPickup(order.id)}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                      data-testid="confirm-pickup-btn"
                    >
                      ✓ Confirm Pickup
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Merchant Ratings */}
        {pendingRatings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="text-yellow-500">⭐</span> Rate Merchants ({pendingRatings.length} pending)
            </h2>
            <div className="space-y-4">
              {pendingRatings.map((order) => (
                <div key={order.id} className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-sm" data-testid="pending-rating-card">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="font-bold text-foreground">{order.merchant_name}</p>
                      <div className="text-sm mt-1">
                        {order.items?.map((item, i) => (
                          <span key={i} className="mr-2">{item.quantity}x {item.brand}</span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Order #{order.id.slice(0, 8)}</p>
                    </div>
                    <Button
                      onClick={() => openRatingDialog(order)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold"
                      data-testid="rate-merchant-btn"
                    >
                      ⭐ Rate Merchant
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {todayAppointments.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Today&apos;s Appointments</h2>
            <div className="space-y-4">
              {todayAppointments.map((appointment) => (
                <div key={appointment.id} className="bg-white border-l-4 border-secondary p-6 rounded-sm shadow-sm" data-testid="today-appointment-card">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-heading font-bold text-foreground">{appointment.service_type}</h3>
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                          appointment.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          appointment.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {appointment.status}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm font-medium mb-4">
                        <p><span className="text-muted-foreground">Time:</span> <span className="font-bold text-foreground text-lg">{appointment.time}</span></p>
                        <p><span className="text-muted-foreground">Customer:</span> <span className="font-bold text-foreground">{appointment.customer_name}</span></p>
                        <p><span className="text-muted-foreground">Phone:</span> <span className="font-mono text-foreground">{appointment.customer_phone}</span></p>
                        <p><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{appointment.customer_address}</span></p>
                        <p><span className="text-muted-foreground">Vehicle:</span> <span className="text-foreground">{appointment.vehicle_info}</span></p>
                        {!appointment.has_own_oil && (
                          <div className="mt-3 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-sm">
                            <p className="font-bold text-sm text-yellow-800 mb-1">⚠️ Bring Oil & Filter</p>
                            <p className="text-sm text-yellow-700">
                              {appointment.needs_recommendation 
                                ? 'Customer needs your recommendation for oil type' 
                                : `Customer requested: ${appointment.preferred_oil_type}`}
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">Customer will pay for oil/filter before service</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {appointment.status === 'confirmed' && (
                          <Button 
                            onClick={() => updateStatus(appointment.id, 'in_progress')}
                            data-testid="start-job-btn"
                            className="btn-primary"
                          >
                            Start Job
                          </Button>
                        )}
                        {appointment.status === 'in_progress' && (
                          <Button 
                            onClick={() => updateStatus(appointment.id, 'completed')}
                            data-testid="complete-job-btn"
                            className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide rounded-sm px-6 py-3"
                          >
                            Complete Job
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Upcoming This Week</h2>
        {upcomingAppointments.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
            <p className="text-xl font-medium text-muted-foreground">No upcoming appointments</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {upcomingAppointments.map((appointment) => (
              <div key={appointment.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
                <h3 className="text-lg font-heading font-bold text-foreground mb-3">{appointment.service_type}</h3>
                <div className="space-y-1 text-sm font-medium">
                  <p><span className="text-muted-foreground">Date:</span> <span className="font-bold text-foreground">{formatDate(appointment.date)}</span></p>
                  <p><span className="text-muted-foreground">Time:</span> <span className="font-bold text-foreground">{appointment.time}</span></p>
                  <p><span className="text-muted-foreground">Customer:</span> <span className="text-foreground">{appointment.customer_name}</span></p>
                  <p><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{appointment.customer_address}</span></p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service Completion Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Complete Service</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="bg-accent p-4 rounded-sm">
                <p className="text-sm text-muted-foreground">Service</p>
                <p className="font-bold text-foreground">{selectedAppointment.service_type}</p>
                <p className="text-sm text-muted-foreground mt-2">Customer</p>
                <p className="font-bold text-foreground">{selectedAppointment.customer_name}</p>
              </div>

              <div>
                <Label htmlFor="service_mileage" className="font-bold uppercase text-sm tracking-wide">
                  Current Vehicle Mileage (km) *
                </Label>
                <Input
                  id="service_mileage"
                  type="number"
                  required
                  data-testid="service-mileage-input"
                  className="input-field mt-2"
                  placeholder="Enter current odometer reading"
                  value={serviceData.service_mileage}
                  onChange={(e) => setServiceData({ ...serviceData, service_mileage: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Used to calculate next service date</p>
              </div>

              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">Oil Type Used</Label>
                <Select
                  value={serviceData.oil_type_used}
                  onValueChange={(value) => setServiceData({ ...serviceData, oil_type_used: value })}
                >
                  <SelectTrigger className="input-field mt-2" data-testid="oil-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Synthetic Oil">Synthetic Oil (6,000km / 5 months)</SelectItem>
                    <SelectItem value="Semi-Synthetic Oil">Semi-Synthetic Oil (4,000km / 4 months)</SelectItem>
                    <SelectItem value="Mineral Oil">Mineral Oil (3,000km / 2 months)</SelectItem>
                    <SelectItem value="High-Mileage Mineral Oil">High-Mileage Mineral Oil (4,000km / 4 months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
                <p className="text-sm text-green-800 font-bold mb-1">Commission Earned</p>
                <p className="text-xs text-green-700">
                  Your earnings will be calculated and credited upon completion based on your technician type.
                </p>
              </div>

              <Button
                onClick={completeService}
                className="btn-primary w-full h-12"
                data-testid="complete-service-btn"
              >
                Complete Service & Log Mileage
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Merchant Rating Dialog */}
      <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Rate Merchant</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-accent p-4 rounded-sm">
                <p className="font-bold text-foreground text-lg">{selectedOrder.merchant_name}</p>
                <div className="text-sm mt-1">
                  {selectedOrder.items?.map((item, i) => (
                    <span key={i} className="mr-2">{item.quantity}x {item.brand}</span>
                  ))}
                </div>
              </div>

              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">Product Quality</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Button
                      key={n}
                      type="button"
                      variant={ratingData.product_quality >= n ? "default" : "outline"}
                      className={`w-10 h-10 ${ratingData.product_quality >= n ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                      onClick={() => setRatingData({ ...ratingData, product_quality: n })}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Quality of oil, filters, etc.</p>
              </div>

              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">Service Quality</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Button
                      key={n}
                      type="button"
                      variant={ratingData.service_quality >= n ? "default" : "outline"}
                      className={`w-10 h-10 ${ratingData.service_quality >= n ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                      onClick={() => setRatingData({ ...ratingData, service_quality: n })}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Helpfulness and professionalism</p>
              </div>

              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">Timeliness</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Button
                      key={n}
                      type="button"
                      variant={ratingData.timeliness >= n ? "default" : "outline"}
                      className={`w-10 h-10 ${ratingData.timeliness >= n ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                      onClick={() => setRatingData({ ...ratingData, timeliness: n })}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Ready on time for pickup</p>
              </div>

              <div>
                <Label className="font-bold uppercase text-sm tracking-wide">Comment (Optional)</Label>
                <Input
                  className="input-field mt-2"
                  placeholder="Any additional feedback..."
                  value={ratingData.comment}
                  onChange={(e) => setRatingData({ ...ratingData, comment: e.target.value })}
                />
              </div>

              <Button
                onClick={submitRating}
                className="btn-primary w-full h-12"
                data-testid="submit-rating-btn"
              >
                Submit Rating
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}